import { DropdownButtonType, List, ListItem, LocallyFilteringList, SingleSelectDropdown, TextLinkWidget } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { classes } from 'typestyle';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import {
  AllowPulseFeatureSectionProps,
  DIGEST_START_TIME_SITE_SETTING,
  PULSE_DIGEST_FREQUENCY_DESCRIPTION_TEST_ID,
  PULSE_DIGEST_FREQUENCY_TABLE_TEST_ID,
  PULSE_DIGEST_SCHEDULE_DROPDOWN_TEST_ID,
  PULSE_EMAIL_NOTIFICATIONS_SITE_SETTING,
  PULSE_EMAIL_NOTIFICATIONS_TEST_ID,
  PULSE_MONTHLY_DAY_DROPDOWN_TEST_ID,
  PULSE_MONTHLY_DAY_OR_WEEK_DROPDOWN_TEST_ID,
  PULSE_MONTHLY_DIGEST_DAY_OR_WEEK_SITE_SETTING,
  PULSE_MONTHLY_DIGEST_DAY_SITE_SETTING,
  PULSE_MONTHLY_WEEKDAY_DROPDOWN_TEST_ID,
  PULSE_MSTEAMS_NOTIFICATIONS_SITE_SETTING,
  PULSE_MSTEAMS_NOTIFICATIONS_TEST_ID,
  PULSE_SLACK_NOTIFICATIONS_SITE_SETTING,
  PULSE_SLACK_NOTIFICATIONS_TEST_ID,
  PULSE_WEEKLY_DIGEST_DAY_SITE_SETTING,
  PULSE_WEEKLY_FREQUENCY_DROPDOWN_TEST_ID
} from '~src/settings/general-settings/components/sections/pulse/PulseFeatureSection';
import {
  alternateRowStyle,
  digestStartTimeDropdownStyle,
  digestStartTimeDropdownWrapperStyle,
  frequencyColumnStyle,
  frequencyDescriptionStyle,
  frequencyTableStyle,
  onColumnStyle,
  slackIntegrationsInfoStyle,
  startTimeColumnStyle,
  tableCellStyle,
  tableDropdownStyle,
  tableHeaderStyle,
  timeZoneNameContainerStyle,
  timeZoneNameStyle,
  warningTextStyle
} from '~src/settings/general-settings/components/sections/pulse/PulseFeatureSectionStyles';
import { dropdownListStyle, learnMoreStyle, SettingsStyles } from '~src/settings/styles/CommonStyles';

const minutesToString = (minutesSinceMidnight: number | undefined): string => {
  if (!minutesSinceMidnight) {
    return '00:00';
  }
  const hours = Math.floor(minutesSinceMidnight / 60);
  const minutes = minutesSinceMidnight - (hours * 60);
  const hoursStr = `${hours < 10 ? '0' : ''}${hours}`;
  const minutesStr = `${minutes < 10 ? '0' : ''}${minutes}`;
  return `${hoursStr}:${minutesStr}`;
};

const numberOfFiveMinuteIncrementsInOneDay = 60 * 24 / 5;

interface DigestScheduleProps {
  timeZoneDisplayName: React.ReactNode;
  isPulseEnabled: boolean;
  weeklyDigestDaysList: ListItem[];
  monthlyDayOrWeekList: ListItem[];
}

export function PulseDigestSchedule(formik: FormikContextType<AllowPulseFeatureSectionProps>): JSX.Element {
  const timeZone = formik.values.settings.useDefaultTimeZone ? 'UTC' : formik.values.settings.timeZone;
  const timeZonesListItems = formik.values.timeZonesListItems;
  const timeZoneDisplayName = timeZonesListItems?.find(item => item.value === timeZone)?.content ?? '';
  const isPulseEnabled = formik.values.settings.pulseEnabled;

  // Feature flag logic
  const showDigestToggles = FeatureFlags.ntbue_ADMIN_SITE_DIGEST_TOGGLEEnabled;
  const showMSTeamsNotifications = FeatureFlags.ntbue_ALLOW_MSTEAMS_NOTIFICATIONSEnabled;

  // Get current monthly digest values (defaults: 0 = day of month, 1 = first day)
  // Convert string values to numbers for proper behavior
  const currentMonthlyDayOrWeek = parseInt(String(formik.values.settings.siteSettingsV2?.pulseMonthlyDigestDayOrWeek ?? 0), 10);
  const currentMonthlyDay = parseInt(String(formik.values.settings.siteSettingsV2?.pulseMonthlyDigestDateOrDay ?? 1), 10);


  // Weekly digest days list (Sunday = 6, Monday = 0, ..., Saturday = 5)
  const weeklyDigestDaysList: ListItem[] = [
    {content: Messages.dayNameSun(), value: '6'},
    {content: Messages.dayNameMon(), value: '0'},
    {content: Messages.dayNameTue(), value: '1'},
    {content: Messages.dayNameWed(), value: '2'},
    {content: Messages.dayNameThu(), value: '3'},
    {content: Messages.dayNameFri(), value: '4'},
    {content: Messages.dayNameSat(), value: '5'}
  ];

  // Monthly digest day or week options
  const monthlyDayOrWeekList: ListItem[] = [
    {content: Messages.pulseDigestScheduleMonthlyDay(), value: '0'},
    {content: Messages.pulseDigestScheduleMonthlyFirst(), value: '1'},
    {content: Messages.pulseDigestScheduleMonthlySecond(), value: '2'},
    {content: Messages.pulseDigestScheduleMonthlyThird(), value: '3'},
    {content: Messages.pulseDigestScheduleMonthlyFourth(), value: '4'},
    {content: Messages.pulseDigestScheduleMonthlyFifth(), value: '5'},
    {content: Messages.pulseDigestScheduleMonthlyLast(), value: '6'}
  ];

  const digestScheduleProps: DigestScheduleProps = {
    timeZoneDisplayName,
    isPulseEnabled,
    weeklyDigestDaysList,
    monthlyDayOrWeekList
  };

  return (
    <div>
      {showDigestToggles && showMSTeamsNotifications && (
        <p style={{ marginBottom: '12px' }}>
          {Messages.pulseNotificationChannelsDescription()}
        </p>
      )}
      {showDigestToggles && PulseEmailNotificationsCheckbox()}
      {showDigestToggles && PulseSlackNotificationsCheckbox()}
      {showDigestToggles && showMSTeamsNotifications && PulseMSTeamsNotificationsCheckbox()}
      <p>{Messages.pulseDigestScheduleSiteSettingDescriptionShort()}</p>
      <DigestStartTimeSection {...digestScheduleProps} />
      <div>
        <p data-tb-test-id={PULSE_DIGEST_FREQUENCY_DESCRIPTION_TEST_ID} className={frequencyDescriptionStyle}>{Messages.pulseDigestScheduleFrequencyDescription()}</p>
        <FrequencyTable {...digestScheduleProps} />
        {currentMonthlyDayOrWeek === 0 && currentMonthlyDay >= 29 && currentMonthlyDay <= 31 && (
          <p className={warningTextStyle}>
            {Messages.pulseDigestScheduleMonthlyDayWarning({ DAY_NUMBER: String(currentMonthlyDay) })}
          </p>
        )}
        {currentMonthlyDayOrWeek === 5 && (
          <p className={warningTextStyle}>
            {Messages.pulseDigestScheduleMonthlyFifthWarning({
            DAY_OF_WEEK: String(weeklyDigestDaysList.find(item => item.value === String(currentMonthlyDay))?.content || Messages.dayNameMon())
          })}
          </p>
        )}
      </div>
    </div>
  );
}

function DigestStartTimeSection(props: DigestScheduleProps): JSX.Element {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();

  function setDigestStartTime(value: string) {
    formik.setFieldValue(DIGEST_START_TIME_SITE_SETTING, parseInt(value, 10));
  }

  const digestStartTimesList: ListItem[] = Array.from(Array(numberOfFiveMinuteIncrementsInOneDay).keys()).map(n => {
    const minutesSinceMidnight = n * 5;
    return {content: minutesToString(minutesSinceMidnight), value: `${minutesSinceMidnight}`};
  });

  const itemFilterFn = (item: ListItem, filter: string) => item.content.toString().includes(filter.toLowerCase());

  return (
    <div className={digestStartTimeDropdownWrapperStyle}>
      <SingleSelectDropdown
        label={Messages.pulseDigestScheduleSiteSettingStartTime()}
        aria-label={Messages.pulseDigestScheduleSiteSettingStartTime()}
        list={<LocallyFilteringList items={digestStartTimesList} className={dropdownListStyle}
                                    itemFilterFn={itemFilterFn}/>}
        onChange={setDigestStartTime}
        type={DropdownButtonType.Outline}
        value={minutesToString(formik.values.settings.siteSettingsV2?.pulseDigestStartTime)}
        className={digestStartTimeDropdownStyle}
        disabled={!props.isPulseEnabled}
        testId={PULSE_DIGEST_SCHEDULE_DROPDOWN_TEST_ID}
      />
      <div className={timeZoneNameContainerStyle}>
        <div className={timeZoneNameStyle}>
          {props.timeZoneDisplayName}
        </div>
      </div>
    </div>
  );
}

function WeeklyFrequencyDropdown(props: DigestScheduleProps): JSX.Element {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();

  // Convert string value to number for proper behavior
  const currentWeeklyDay = parseInt(String(formik.values.settings.siteSettingsV2?.pulseWeeklyDigestDay ?? 0), 10);

  function setWeeklyDigestDay(value: string) {
    formik.setFieldValue(PULSE_WEEKLY_DIGEST_DAY_SITE_SETTING, parseInt(value, 10));
  }

  return (
    <SingleSelectDropdown
      label=""
      aria-label="Select day for weekly digests"
      list={<List items={props.weeklyDigestDaysList} />}
      onChange={setWeeklyDigestDay}
      type={DropdownButtonType.Outline}
      value={String(currentWeeklyDay)}
      className={tableDropdownStyle}
      disabled={!props.isPulseEnabled}
      testId={PULSE_WEEKLY_FREQUENCY_DROPDOWN_TEST_ID}
    />
  );
}

function MonthlyFrequencyDropdowns(props: DigestScheduleProps): JSX.Element {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();

  // Convert string values to numbers for proper behavior
  const currentMonthlyDayOrWeek = parseInt(String(formik.values.settings.siteSettingsV2?.pulseMonthlyDigestDayOrWeek ?? 0), 10);
  const currentMonthlyDay = parseInt(String(formik.values.settings.siteSettingsV2?.pulseMonthlyDigestDateOrDay ?? 1), 10);

  function setMonthlyDigestDayOrWeek(value: string) {
    const newDayOrWeek = parseInt(value, 10);
    formik.setFieldValue(PULSE_MONTHLY_DIGEST_DAY_OR_WEEK_SITE_SETTING, newDayOrWeek);

    if (newDayOrWeek === 0) {
      formik.setFieldValue(PULSE_MONTHLY_DIGEST_DAY_SITE_SETTING, 1);
    } else if (currentMonthlyDayOrWeek === 0 && newDayOrWeek !== 0) {
      formik.setFieldValue(PULSE_MONTHLY_DIGEST_DAY_SITE_SETTING, 0);
    }
  }

  function setMonthlyDigestDay(value: string) {
    formik.setFieldValue(PULSE_MONTHLY_DIGEST_DAY_SITE_SETTING, parseInt(value, 10));
  }

  const itemFilterFn = (item: ListItem, filter: string) => item.content.toString().includes(filter.toLowerCase());

  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      <SingleSelectDropdown
        label=""
        aria-label="Select monthly digest type"
        list={<List items={props.monthlyDayOrWeekList} />}
        onChange={setMonthlyDigestDayOrWeek}
        type={DropdownButtonType.Outline}
        value={String(currentMonthlyDayOrWeek)}
        disabled={!props.isPulseEnabled}
        testId={PULSE_MONTHLY_DAY_OR_WEEK_DROPDOWN_TEST_ID}
        className={tableDropdownStyle}
      />
      <div key={`monthly-mode-${currentMonthlyDayOrWeek}`}>
        {currentMonthlyDayOrWeek === 0 ? (
          <SingleSelectDropdown
            label=""
            aria-label="Select day of month"
            list={<LocallyFilteringList items={Array.from({length: 31}, (_, i) => ({
              content: String(i + 1),
              value: String(i + 1)
            })).concat([{
              content: Messages.pulseDigestScheduleMonthlyLast(),
              value: '32'
            }])} className={dropdownListStyle}
                                        itemFilterFn={itemFilterFn} />}
            onChange={setMonthlyDigestDay}
            type={DropdownButtonType.Outline}
            value={String(Math.max(1, Math.min(32, currentMonthlyDay)))}
            disabled={!props.isPulseEnabled}
            testId={PULSE_MONTHLY_DAY_DROPDOWN_TEST_ID}
            className={tableDropdownStyle}
          />
        ) : (
          <SingleSelectDropdown
            label=""
            aria-label="Select day of week"
            list={<List items={props.weeklyDigestDaysList} />}
            onChange={(value) => setMonthlyDigestDay(value)}
            type={DropdownButtonType.Outline}
            value={String(Math.max(0, Math.min(6, currentMonthlyDay)))}
            disabled={!props.isPulseEnabled}
            testId={PULSE_MONTHLY_WEEKDAY_DROPDOWN_TEST_ID}
            className={tableDropdownStyle}
          />
        )}
      </div>
    </div>
  );
}

function FrequencyTable(props: DigestScheduleProps): JSX.Element {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();

  const startTimeDisplay = `${minutesToString(formik.values.settings.siteSettingsV2?.pulseDigestStartTime)} ${props.timeZoneDisplayName}`;

  return (
    <table data-tb-test-id={PULSE_DIGEST_FREQUENCY_TABLE_TEST_ID} className={frequencyTableStyle}>
      <thead>
        <tr>
          <th className={classes(tableHeaderStyle, frequencyColumnStyle)}>{Messages.pulseDigestScheduleFrequencyTableHeaderFrequency()}</th>
          <th className={classes(tableHeaderStyle, startTimeColumnStyle)}>{Messages.pulseDigestScheduleFrequencyTableHeaderStartTime()}</th>
          <th className={classes(tableHeaderStyle, onColumnStyle)}>{Messages.pulseDigestScheduleFrequencyTableHeaderOn()}</th>
        </tr>
      </thead>
      <tbody>
        <tr className={classes(tableCellStyle, alternateRowStyle)}>
          <td className={tableCellStyle}>{Messages.pulseDigestScheduleFrequencyDaily()}</td>
          <td className={tableCellStyle}>{startTimeDisplay}</td>
          <td className={tableCellStyle}>{Messages.pulseDigestScheduleEveryDay()}</td>
        </tr>
        <tr>
          <td className={tableCellStyle}>{Messages.pulseDigestScheduleFrequencyWeekly()}</td>
          <td className={tableCellStyle}>{startTimeDisplay}</td>
          <td className={tableCellStyle}>
            <WeeklyFrequencyDropdown {...props} />
          </td>
        </tr>
        <tr className={classes(tableCellStyle, alternateRowStyle)}>
          <td className={tableCellStyle}>{Messages.pulseDigestScheduleFrequencyMonthly()}</td>
          <td className={tableCellStyle}>{startTimeDisplay}</td>
          <td className={tableCellStyle}>
            <MonthlyFrequencyDropdowns {...props} />
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function PulseEmailNotificationsCheckbox(): JSX.Element {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();

  // Convert string value to boolean for proper checkbox behavior
  const currentValue = formik.values.settings.siteSettingsV2?.pulseEmailNotificationsEnabled;
  const booleanValue = currentValue === 'true' || currentValue === true;
  const isPulseEnabled = formik.values.settings.pulseEnabled;
  const showMSTeamsNotifications = FeatureFlags.ntbue_ALLOW_MSTEAMS_NOTIFICATIONSEnabled;

  return (
    <div className={classes(SettingsStyles.checkboxGroup)}>
      <FormikCheckbox
        name={PULSE_EMAIL_NOTIFICATIONS_SITE_SETTING}
        label={showMSTeamsNotifications ? Messages.email() : Messages.pulseEmailNotificationsEnabled()}
        testId={PULSE_EMAIL_NOTIFICATIONS_TEST_ID}
        checked={booleanValue}
        disabled={!isPulseEnabled}
        onChange={(e) => {
          formik.setFieldValue(PULSE_EMAIL_NOTIFICATIONS_SITE_SETTING, e.target.checked);
        }}
      />
    </div>
  );
}

function PulseSlackNotificationsCheckbox(): JSX.Element {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();

  // Convert string value to boolean for proper checkbox behavior
  const currentValue = formik.values.settings.siteSettingsV2?.pulseSlackNotificationsEnabled;
  const booleanValue = currentValue === 'true' || currentValue === true;
  const isPulseEnabled = formik.values.settings.pulseEnabled;
  const showMSTeamsNotifications = FeatureFlags.ntbue_ALLOW_MSTEAMS_NOTIFICATIONSEnabled;

  // Get the URL for the integrations tab
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  const integrationsUrl = siteUrls.settingsIntegrationsUrl();

  return (
    <div className={classes(SettingsStyles.checkboxGroup)} style={showMSTeamsNotifications ? { marginBottom: 0 } : {}}>
      <FormikCheckbox
        name={PULSE_SLACK_NOTIFICATIONS_SITE_SETTING}
        label={showMSTeamsNotifications ? Messages.slack() : Messages.pulseSlackNotificationsEnabled()}
        testId={PULSE_SLACK_NOTIFICATIONS_TEST_ID}
        checked={booleanValue}
        disabled={!isPulseEnabled}
        style={showMSTeamsNotifications ? { marginBottom: 0 } : {}}
        onChange={(e) => {
          formik.setFieldValue(PULSE_SLACK_NOTIFICATIONS_SITE_SETTING, e.target.checked);
        }}
      />
      <p className={slackIntegrationsInfoStyle} data-tb-test-id="pulse-slack-integrations-info" style={showMSTeamsNotifications ? { marginTop: 0, marginBottom: 0 } : {}}>
        {showMSTeamsNotifications ? Messages.pulseSlackNotificationsSetupTextShort() : Messages.pulseSlackNotificationsSetupText()}
        <TextLinkWidget
          href={integrationsUrl}
          target="_self"
          testId="pulse-slack-integrations-link"
          className={learnMoreStyle}
        >
          {Messages.pulseSlackNotificationsIntegrationsTab()}
        </TextLinkWidget>
      </p>
    </div>
  );
}

function PulseMSTeamsNotificationsCheckbox(): JSX.Element {
  const formik = useFormikContext<AllowPulseFeatureSectionProps>();

  // Convert string value to boolean for proper checkbox behavior
  const currentValue = formik.values.settings.siteSettingsV2?.pulseMSTeamsNotificationsEnabled;
  const booleanValue = currentValue === 'true' || currentValue === true;
  const isPulseEnabled = formik.values.settings.pulseEnabled;

  return (
    <div className={classes(SettingsStyles.checkboxGroup)} style={{ marginBottom: 0 }}>
      <FormikCheckbox
        name={PULSE_MSTEAMS_NOTIFICATIONS_SITE_SETTING}
        label={Messages.microsoftTeams()}
        testId={PULSE_MSTEAMS_NOTIFICATIONS_TEST_ID}
        checked={booleanValue}
        disabled={!isPulseEnabled}
        style={{ marginBottom: 0 }}
        onChange={(e) => {
          formik.setFieldValue(PULSE_MSTEAMS_NOTIFICATIONS_SITE_SETTING, e.target.checked);
        }}
      />
      <p className={slackIntegrationsInfoStyle} data-tb-test-id="pulse-msteams-integrations-info" style={{ marginTop: 0, marginBottom: 0 }}>
        {Messages.pulseMSTeamsNotificationsSetupText()}
        <TextLinkWidget
          href={HelpService.getHelpUrlByCategory('online-ms-teams-setup')} // Dummy link as requested
          target="_blank"
          testId="pulse-msteams-integrations-link"
          className={learnMoreStyle}
        >
          {Messages.pulseMSTeamsNotificationsLearnMore()}
        </TextLinkWidget>
      </p>
    </div>
  );
}
